import { Validators } from "@angular/forms";
import {
  HardFormFieldTypeEnum,
  HardTableColumnTypeEnum,
  IHardFormComponentParams,
  IHardFormField,
  IHardFormSelectParams,
} from "@hardcode-kz/ui";
import {
  ISetting,
  SettingNoneParsedTypes,
  SettingTypeEnum,
} from "@models/core/setting";
import { IDicBase } from "@models/dictionary/dic-base";
import { AdministrationEndpoints } from "@dmms/shared/data-access/administration/types/administration-endpoints";
import {
  IBaseConfig,
  IFormFieldFilter,
  ITable,
} from "@models/form/base-config";
import { AdminSettingsTypeFieldComponent } from "@modules/admin/modules/settings/admin-settings-type-field/admin-settings-type-field.component";
import { LangEnum } from "@models/interface-management/localization";
import { TranslationFieldPartialValidator } from "@static/fields/fields-constant";

export type DataTypeOption = {
  id: SettingTypeEnum;
  name: keyof typeof SettingTypeEnum;
};

const dataTypeOptions: DataTypeOption[] = [
  { id: SettingTypeEnum.Text, name: "Text" },
  { id: SettingTypeEnum.LongText, name: "LongText" },
  { id: SettingTypeEnum.Number, name: "Number" },
  { id: SettingTypeEnum.Checkbox, name: "Checkbox" },
  { id: SettingTypeEnum.Array, name: "Array" },
  { id: SettingTypeEnum.Json, name: "Json" },
  { id: SettingTypeEnum.Enum, name: "Enum" },
  { id: SettingTypeEnum.Timespan, name: "Timespan" },
];

export class SettingConfig implements IBaseConfig {
  version = 1;
  dicType = AdministrationEndpoints.SettingItems;
  title = "base.settings";
  icon = "icon-settings";
  showRestore = true;
  dataIdKey = "setting";

  fields: IHardFormField[] = [
    {
      name: "key",
      title: "dicTable.code",
      type: HardFormFieldTypeEnum.Input,
      params: {
        validators: [Validators.required],
      },
    },
    {
      name: "name",
      params: {
        direction: "row",
        fields: TranslationFieldPartialValidator(
          "dicTable.name",
          HardFormFieldTypeEnum.Input,
          [LangEnum.RU],
        ),
      },
    },
    {
      name: "dataType",
      title: "dicTable.dataType",
      type: HardFormFieldTypeEnum.Select,
      params: {
        options: [...dataTypeOptions],
        validators: [Validators.required],
        value: SettingTypeEnum.Text,
      } as IHardFormSelectParams,
    },
    {
      name: "settingGroup",
      title: "dicTable.group",
      type: HardFormFieldTypeEnum.Dictionary,
      params: {
        dicType: "api/v1/" + AdministrationEndpoints.SettingGroup,
      } as IHardFormSelectParams,
    },
    {
      name: "description",
      params: {
        direction: "column",
        fields: [
          ...TranslationFieldPartialValidator(
            "dicTable.description",
            HardFormFieldTypeEnum.Textarea,
            [LangEnum.RU],
            { isAutoHeight: true } as any,
          ),
        ],
      },
    },
    {
      name: "value",
      title: "Component 1",
      type: HardFormFieldTypeEnum.Component,
      params: {
        component: AdminSettingsTypeFieldComponent,
      } as IHardFormComponentParams,
    },
    {
      name: "isRequired",
      title: "dicTable.required",
      type: HardFormFieldTypeEnum.Checkbox,
      value: false,
    },
    {
      name: "isShowOnInterface",
      title: "dicTable.showOnInterface",
      type: HardFormFieldTypeEnum.Checkbox,
      value: false,
    },
    {
      name: "isReadonly",
      title: "dicTable.readonly",
      type: HardFormFieldTypeEnum.Checkbox,
      value: false,
    },
    {
      name: "isEncrypt",
      title: "dicTable.encrypted",
      type: HardFormFieldTypeEnum.Checkbox,
      value: false,
    },
    {
      name: "isUnauthorizedAccessAllowed",
      title: "dicTable.unauthorizedAccessAllowed",
      type: HardFormFieldTypeEnum.Checkbox,
      value: false,
    },
  ];

  tableConfig: ITable<ISetting[]> = {
    showDefaultActions: true,
    tableColumns: "40px 1fr 1fr 1fr auto",
    columns: [
      {
        name: "index",
        title: "#",
        type: HardTableColumnTypeEnum.Index,
      },
      {
        name: "key",
        title: "dicTable.code",
        type: HardTableColumnTypeEnum.Html,
      },
      {
        name: "name",
        title: "dicTable.name",
        type: HardTableColumnTypeEnum.Translate,
      },
      {
        name: "name",
        title: "dicTable.group",
        parent: "settingGroup",
        type: HardTableColumnTypeEnum.Parent,
      },
    ],
  };

  filterTemplateColumns = "1fr 220px";
  filterFields: IFormFieldFilter<IHardFormSelectParams>[] = [
    {
      type: HardFormFieldTypeEnum.Input,
      name: "searchTerm",
      placeholder: "base.search",
      value: "",
      outside: true,
      params: {
        isAutoFocus: true,
      },
    },
    {
      name: "settingGroup",
      outside: true,
      type: HardFormFieldTypeEnum.Dictionary,
      placeholder: "base.allGroups",
      value: null,
      params: {
        dicType: "api/v1/" + AdministrationEndpoints.SettingGroup,
      },
    },
  ];

  filterCallback = (filterData: {
    searchTerm: string;
    settingGroup: IDicBase;
  }) => {
    return {
      searchTerm: filterData?.searchTerm || "",
      settingGroupId: filterData?.settingGroup?.id || null,
    };
  };

  saveCallback(data: ISetting) {
    if (data.settingGroup?.id == null || data.settingGroup?.name == null) {
      data.settingGroup = null;
    }

    if (data.dataType === SettingTypeEnum.Timespan) {
      return {
        ...data,
        value:
          data.value !== undefined && data.value !== null && data.value !== ""
            ? String(timespanToSeconds(data.value))
            : "",
      };
    }

    if (SettingNoneParsedTypes.includes(data.dataType)) {
      return data;
    }

    return { ...data, value: JSON.stringify(data.value) };
  }

  getCallback(data: ISetting) {
    if (data.dataType === SettingTypeEnum.Timespan) {
      return {
        ...data,
        value:
          data.value !== undefined && data.value !== null && data.value !== ""
            ? secondsToTimespan(data.value)
            : "",
      };
    }

    if (SettingNoneParsedTypes.includes(data.dataType)) {
      return data;
    }

    return {
      ...data,
      value: data?.value ? JSON.parse(data.value) : data?.value,
    };
  }
}

function timespanToSeconds(val: any): number {
  if (val === null || val === undefined || val === "") {
    return 0;
  }
  if (typeof val === "number") {
    return val;
  }
  const str = String(val).trim();
  if (!str) {
    return 0;
  }

  const parts = str.split(/\D+/).filter((p) => p !== "");
  if (parts.length > 1) {
    const nums = parts.map((p) => parseInt(p, 10) || 0);
    if (nums.length === 4) {
      const [d, h, m, s] = nums;
      return d * 86400 + h * 3600 + m * 60 + s;
    }
    if (nums.length === 3) {
      const [h, m, s] = nums;
      return h * 3600 + m * 60 + s;
    }
    if (nums.length === 2) {
      const [m, s] = nums;
      return m * 60 + s;
    }
    return nums[0] || 0;
  }

  const len = str.length;
  const secStr = len >= 2 ? str.slice(len - 2) : str;
  const minStr =
    len >= 4
      ? str.slice(len - 4, len - 2)
      : len > 2
        ? str.slice(0, len - 2)
        : "0";
  const hrStr =
    len >= 6
      ? str.slice(len - 6, len - 4)
      : len > 4
        ? str.slice(0, len - 4)
        : "0";
  const dayStr = len >= 8 ? str.slice(0, len - 6) : "0";

  const s = parseInt(secStr, 10) || 0;
  const m = parseInt(minStr, 10) || 0;
  const h = parseInt(hrStr, 10) || 0;
  const d = parseInt(dayStr, 10) || 0;

  return d * 86400 + h * 3600 + m * 60 + s;
}

function secondsToTimespan(val: any): string {
  if (val === null || val === undefined || val === "") {
    return "";
  }
  const seconds = parseInt(val, 10);
  if (isNaN(seconds)) {
    return "";
  }

  const hours = Math.floor(seconds / 3600);
  const remainingAfterHours = seconds % 3600;
  const minutes = Math.floor(remainingAfterHours / 60);
  const secs = remainingAfterHours % 60;

  const pad = (num: number) => String(num).padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
}
